import { useEffect, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Search, Star, Calendar, Mail, Phone, ChevronRight, Plus, Minus, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [pointsForm, setPointsForm] = useState({ amount: '', description: '', note: '' });

  const load = () => api.get('/admin/clients').then(r => setClients(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/clients/${uid}`);
      setDetail(data);
      setSelectedClient(uid);
    } catch { toast.error('Erreur chargement fiche'); }
  };

  const handlePoints = async () => {
    try {
      await api.post(`/admin/clients/${selectedClient}/points`, {
        amount: parseInt(pointsForm.amount),
        description: pointsForm.description,
        note: pointsForm.note
      });
      toast.success('Points ajustés');
      setPointsOpen(false);
      openDetail(selectedClient);
      load();
    } catch { toast.error('Erreur'); }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.phone?.includes(s);
  });

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-clients-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Clients</h1>
        <div className="flex gap-2">
          <a href="#" onClick={async (e) => { e.preventDefault(); try { const resp = await api.get('/admin/clients/export', { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([resp.data])); const a = document.createElement('a'); a.href = url; a.download = 'clients-rsx-concept.csv'; a.click(); } catch { toast.error('Erreur export'); } }}>
            <Button variant="outline" className="border-white/10 rounded-full px-4 text-xs" data-testid="export-csv-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
          </a>
          <Badge className="bg-[#D4AF37]/15 text-[#D4AF37]"><Users className="w-3 h-3 mr-1" /> {clients.length}</Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Client list */}
        <div className="lg:col-span-2">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client..."
              data-testid="client-search" className="pl-10 bg-[#121212] border-white/10 rounded-xl" />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map(c => (
              <button key={c.user_id} onClick={() => openDetail(c.user_id)} data-testid={`client-${c.user_id}`}
                className={`w-full text-left bg-[#121212] border rounded-xl p-3 flex items-center gap-3 transition-colors ${selectedClient === c.user_id ? 'border-[#D4AF37]/40' : 'border-white/5 hover:border-white/15'}`}>
                <div className="bg-[#D4AF37]/10 p-2 rounded-lg shrink-0"><Users className="w-4 h-4 text-[#D4AF37]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[10px] text-[#A1A1AA] truncate">{c.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{c.points_solde || 0}</div>
                  <div className="text-[9px] text-[#A1A1AA]">pts</div>
                </div>
                <ChevronRight className="w-3 h-3 text-[#A1A1AA] shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Client detail 360 */}
        <div className="lg:col-span-3">
          {!detail ? (
            <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center text-[#A1A1AA]">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{"Sélectionnez un client pour voir sa fiche"}</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="client-detail">
              {/* Client header */}
              <div className="bg-[#121212] border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{detail.user.name}</h2>
                    <div className="flex gap-3 text-xs text-[#A1A1AA] mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{detail.user.email}</span>
                      {detail.user.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{detail.user.phone}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{detail.user.points_solde || 0}</div>
                    <div className="text-[10px] text-[#A1A1AA]">points</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setPointsForm({ amount: '100', description: 'Bonus', note: '' }); setPointsOpen(true); }}
                    className="bg-[#10B981] text-white hover:bg-[#10B981]/80 rounded-full text-xs" data-testid="add-points-btn">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter points
                  </Button>
                  <Button size="sm" onClick={() => { setPointsForm({ amount: '-50', description: 'Ajustement', note: '' }); setPointsOpen(true); }}
                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full text-xs" data-testid="remove-points-btn">
                    <Minus className="w-3 h-3 mr-1" /> Retirer points
                  </Button>
                </div>
              </div>

              {/* Points transactions */}
              <div className="bg-[#121212] border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Historique points</h3>
                {detail.points_transactions.length === 0 ? (
                  <p className="text-xs text-[#A1A1AA] text-center py-4">Aucune transaction</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {detail.points_transactions.slice(0, 10).map(tx => (
                      <div key={tx.tx_id} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                        <div><div className="text-xs">{tx.description}</div><div className="text-[9px] text-[#A1A1AA]">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</div></div>
                        <span className={`text-xs font-bold ${tx.amount > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} style={{ fontFamily: 'JetBrains Mono' }}>{tx.amount > 0 ? '+' : ''}{tx.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reservations */}
              <div className="bg-[#121212] border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>{"Réservations"}</h3>
                {detail.reservations.length === 0 ? (
                  <p className="text-xs text-[#A1A1AA] text-center py-4">Aucune réservation</p>
                ) : (
                  <div className="space-y-2">
                    {detail.reservations.slice(0, 5).map(r => (
                      <div key={r.reservation_id} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                        <div><div className="text-xs">{r.prestation_type || r.type}</div><div className="text-[9px] text-[#A1A1AA]">{new Date(r.created_at).toLocaleDateString('fr-FR')}</div></div>
                        <Badge className="text-[9px]">{r.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Points Dialog */}
      <Dialog open={pointsOpen} onOpenChange={setPointsOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Ajuster les points</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Input type="number" value={pointsForm.amount} onChange={e => setPointsForm(p => ({ ...p, amount: e.target.value }))}
              placeholder="Nombre de points" data-testid="points-amount" className="bg-[#121212] border-white/10 rounded-xl" /></div>
            <div><Input value={pointsForm.description} onChange={e => setPointsForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Description (visible client)" data-testid="points-desc" className="bg-[#121212] border-white/10 rounded-xl" /></div>
            <div><Input value={pointsForm.note} onChange={e => setPointsForm(p => ({ ...p, note: e.target.value }))}
              placeholder="Note interne (admin)" data-testid="points-note" className="bg-[#121212] border-white/10 rounded-xl" /></div>
            <Button onClick={handlePoints} data-testid="points-submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">Confirmer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
